/**
 * Client de l'API de mise en relation — L4.
 *
 * **Aucun chemin de ce module n'est mis en cache par le service worker** (invariant 17) : tous sont
 * personnalisés. La liste blanche de `vite.config.ts` ne porte que les référentiels de L3 —
 * quartiers et ancres — et rien de ce fichier ne doit l'y rejoindre. Un fil resservi depuis le
 * cache après un changement de compte afficherait la conversation de quelqu'un d'autre.
 *
 * **Le corps des messages arrive déjà projeté.** Le masquage est appliqué côté serveur, à la
 * lecture ; ce module ne masque rien et ne doit jamais essayer de le faire — une seconde
 * implémentation du masque divergerait de la première, en silence.
 */

import { appeler } from './api'

export type Occupation = 'Student' | 'Employee' | 'SelfEmployed' | 'Other'
export type NiveauEtude = 'Bac' | 'BacPlusDeux' | 'Licence' | 'Master' | 'Doctorat' | 'Autre'
export type TypeContrat = 'Cdi' | 'Cdd' | 'Interim' | 'Independant' | 'Autre'
export type TrancheRevenu =
  | 'Undisclosed'
  | 'UnderThreeThousand'
  | 'ThreeToFiveThousand'
  | 'FiveToEightThousand'
  | 'EightToTwelveThousand'
  | 'OverTwelveThousand'
export type TypeGarant = 'None' | 'Family' | 'Employer' | 'Institution' | 'Other'
export type NatureRecherche = 'EntirePlace' | 'RoomInSharedHousing' | 'FormGroup'
export type ExigenceEquipement = 'NiceToHave' | 'MustHave' | 'Exclude'

export type StatutCandidature =
  | 'Draft'
  | 'Submitted'
  | 'UnderHouseholdVote'
  | 'Shortlisted'
  | 'VisitScheduled'
  | 'Accepted'
  | 'Rejected'
  | 'Withdrawn'
  | 'Expired'
  | 'ConvertedToLease'

export type OrigineCandidature = 'Search' | 'Match' | 'Direct' | 'Invitation'
export type ModeVisite = 'InPerson' | 'Video'
export type StatutVisite = 'Proposed' | 'Confirmed' | 'Rescheduled' | 'Completed' | 'Cancelled' | 'NoShow'
export type StatutFil = 'Active' | 'Archived' | 'Blocked'

export interface Dossier {
  occupation: Occupation
  institutionName: string | null
  studyLevel: NiveauEtude | null
  studyYear: number | null
  employmentSector: string | null
  contractType: TypeContrat | null
  seniorityMonths: number | null
  /** Une TRANCHE, jamais un montant : le revenu exact n'est ni demandé ni stocké. */
  incomeBand: TrancheRevenu
  maxBudgetAllInclusive: number
  currencyCode: string
  hasGuarantor: boolean
  /** La NATURE du garant. Aucune identification de la personne (invariant 8). */
  guarantorType: TypeGarant | null
  moveInWindowStart: string
  moveInWindowEnd: string
  desiredDurationMonths: number
  searchKind: NatureRecherche
  /** La ville où l'on cherche — code du référentiel (D44). Le matching ne la franchit jamais. Nul = la ville par défaut. */
  cityCode: string | null
  maxTravelMinutes: number | null
  currentSituation: string | null
  about: string | null
  completedAt: string | null
  amenityPreferences: { amenityCode: string; requirement: ExigenceEquipement }[] | null
}

export interface Fil {
  id: string
  kind: 'ListingInquiry' | 'ApplicationThread' | 'HouseholdThread' | 'SupportThread'
  status: StatutFil
  listingId: string | null
  listingTitle: string | null
  listingCoverMediaId: string | null
  otherUserId: string | null
  otherDisplayName: string | null
  lastMessageAt: string
  lastMessagePreview: string | null
  unreadCount: number
  /** Levé à la confirmation d'une visite. L'écran doit l'expliquer, pas le subir. */
  contactDetailsVisible: boolean
}

export interface Message {
  id: string
  senderUserId: string
  senderDisplayName: string | null
  body: string
  /** Le serveur a masqué : l'interface le dit, elle ne le devine pas au caractère de remplacement. */
  wasMasked: boolean
  isDeleted: boolean
  sentAt: string
}

export interface Candidature {
  id: string
  listingId: string | null
  listingTitle: string | null
  listingCoverMediaId: string | null
  seekerUserId: string
  seekerDisplayName: string | null
  status: StatutCandidature
  origin: OrigineCandidature
  motivationMessage: string | null
  rejectionReasonCode: string | null
  submittedAt: string | null
  decidedAt: string | null
  createdAt: string
  /** Ce que le propriétaire lit du dossier : la tranche, jamais un montant ; la nature du garant, jamais son nom. */
  seekerIncomeBand: TrancheRevenu | null
  seekerGuarantorType: TypeGarant | null
  /** Le score du moteur (0 à 1) pour ce candidat sur cette annonce, s'il est calculé. */
  matchScore: number | null
  /** Le loyer charges comprises de l'annonce, pour la carte. */
  listingRent: number | null
  listingCurrencyCode: string | null
}

export interface Creneau {
  id: string
  startsAt: string
  endsAt: string
  isSelected: boolean
}

export interface Visite {
  id: string
  listingId: string
  listingTitle: string | null
  /** La couverture de l'annonce — 13/09/2026, comme sur `Candidature`. */
  listingCoverMediaId: string | null
  applicationId: string | null
  requestedByUserId: string
  /** Un nom AFFICHÉ, jamais un nom complet ni une coordonnée : le masquage de L4 tient. */
  requestedByDisplayName: string | null
  hostUserId: string
  hostDisplayName: string | null
  mode: ModeVisite
  status: StatutVisite
  selectedSlotId: string | null
  slots: Creneau[]
}

export interface Notification {
  id: string
  eventType: string
  conversationId: string | null
  applicationId: string | null
  listingId: string | null
  /**
   * La phrase sous le titre — le dernier message reçu du fil, sinon le titre de l'annonce.
   *
   * **Elle n'est pas stockée dans la notification** : l'API la relit à la source à chaque
   * requête, avec les droits du demandeur et le masquage du fil. Nulle quand la source n'a rien
   * à montrer — un rappel de loyer n'a pas d'extrait.
   */
  preview: string | null
  createdAt: string
  readAt: string | null
}

export interface Page<T> {
  items: T[]
  page: number
  size: number
  total: number
}

// ---------------------------------------------------------------------------
// Dossier déclaratif
// ---------------------------------------------------------------------------

/** `null` quand aucun dossier n'existe encore : l'API répond 204, pas 404. */
export async function lireDossier(): Promise<Dossier | null> {
  const dossier = await appeler<Dossier | undefined>('/me/seeker-profile')

  return dossier ?? null
}

export function enregistrerDossier(dossier: Dossier): Promise<void> {
  return appeler('/me/seeker-profile', { method: 'PUT', body: JSON.stringify(dossier) })
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/**
 * Les fils du compte.
 *
 * `archives` rend les fils ARCHIVÉS et eux seuls — deux listes qui ne se mélangent jamais. Archiver
 * veut dire « hors de ma vue » : les faire réapparaître au milieu des autres annulerait le geste.
 */
export function listerFils(page = 1, taille = 20, archives = false): Promise<Page<Fil>> {
  return appeler(`/conversations?page=${page}&size=${taille}&archived=${archives}`)
}

export function lireFil(id: string): Promise<Fil> {
  return appeler(`/conversations/${id}`)
}

export function ouvrirFil(annonceId: string, corps: string): Promise<{ id: string }> {
  return appeler('/conversations', {
    method: 'POST',
    body: JSON.stringify({ listingId: annonceId, body: corps }),
  })
}

export function listerMessages(id: string, page = 1, taille = 50): Promise<Page<Message>> {
  return appeler(`/conversations/${id}/messages?page=${page}&size=${taille}`)
}

export function envoyerMessage(id: string, corps: string): Promise<void> {
  return appeler(`/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ body: corps }) })
}

export function marquerFilLu(id: string): Promise<void> {
  return appeler(`/conversations/${id}/read`, { method: 'POST' })
}

export function archiverFil(id: string): Promise<void> {
  return appeler(`/conversations/${id}/archive`, { method: 'POST' })
}

// ---------------------------------------------------------------------------
// Candidatures
// ---------------------------------------------------------------------------

export function candidater(
  annonceId: string,
  motivation: string | null,
  origine: OrigineCandidature = 'Search',
): Promise<{ id: string }> {
  return appeler('/applications', {
    method: 'POST',
    body: JSON.stringify({ listingId: annonceId, motivation, origin: origine }),
  })
}

export function mesCandidatures(page = 1, taille = 20): Promise<Page<Candidature>> {
  return appeler(`/me/applications?page=${page}&size=${taille}`)
}

export function retirerCandidature(id: string): Promise<void> {
  return appeler(`/applications/${id}`, { method: 'DELETE' })
}

export function candidaturesRecues(annonceId: string, page = 1, taille = 20): Promise<Page<Candidature>> {
  return appeler(`/listings/${annonceId}/applications?page=${page}&size=${taille}`)
}

/** Toutes les candidatures reçues, sur toutes mes annonces — l'onglet propriétaire de « Candidatures ». */
export function toutesCandidaturesRecues(page = 1, taille = 50): Promise<Page<Candidature>> {
  return appeler(`/me/received-applications?page=${page}&size=${taille}`)
}

export function preselectionner(id: string): Promise<void> {
  return appeler(`/applications/${id}/shortlist`, { method: 'POST' })
}

export function accepter(id: string): Promise<void> {
  return appeler(`/applications/${id}/accept`, { method: 'POST' })
}

export function refuser(id: string, motif: string, note: string | null = null): Promise<void> {
  return appeler(`/applications/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode: motif, note }),
  })
}

// ---------------------------------------------------------------------------
// Visites
// ---------------------------------------------------------------------------

export function mesVisites(): Promise<Visite[]> {
  return appeler('/me/visits')
}

export function proposerVisite(
  annonceId: string,
  candidatureId: string | null,
  mode: ModeVisite,
  creneaux: { startsAt: string; endsAt: string }[],
): Promise<{ id: string }> {
  return appeler('/visits', {
    method: 'POST',
    body: JSON.stringify({ listingId: annonceId, applicationId: candidatureId, mode, slots: creneaux }),
  })
}

/** **Ce geste lève le masquage des coordonnées du fil.** L'écran doit le dire avant, pas après. */
export function confirmerVisite(id: string, creneauId: string): Promise<void> {
  return appeler(`/visits/${id}/confirm`, { method: 'POST', body: JSON.stringify({ slotId: creneauId }) })
}

export function replanifierVisite(
  id: string,
  creneaux: { startsAt: string; endsAt: string }[],
): Promise<void> {
  return appeler(`/visits/${id}/reschedule`, { method: 'POST', body: JSON.stringify({ slots: creneaux }) })
}

export function annulerVisite(id: string, motif: string): Promise<void> {
  return appeler(`/visits/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reasonCode: motif }) })
}

export function constaterVisite(id: string, issue: StatutVisite, notes: string | null = null): Promise<void> {
  return appeler(`/visits/${id}/outcome`, { method: 'POST', body: JSON.stringify({ outcome: issue, notes }) })
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function listerNotifications(page = 1, taille = 30): Promise<Page<Notification>> {
  return appeler(`/me/notifications?page=${page}&size=${taille}`)
}

export function compterNonLues(): Promise<{ unread: number }> {
  return appeler('/me/notifications/unread-count')
}

export function marquerNotificationsLues(ids: string[]): Promise<void> {
  return appeler('/me/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) })
}
